import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import CustomTextInput from '../../../components/CustomTextInput';
import { showSnackbar } from '../../../api/snackbarSlice';
import { isTrnNameMatched, resolveTrnRegistryNames, useVerifyTrnHandler } from '../commonDataHandlers/verifyTrnHandler';
import TrnVerifyResultsModal from './TrnVerifyResultsModal';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';

const TRN_PATTERN = /^1\d{12}03$/;
export default function TrnVatField({
  control,
  errors,
  setValue,
  setError,
  clearErrors,
  watch,
  readOnly = false,
  name = 'party_tax_scheme.company_id',
  partyNameField = 'party_name',
  label = 'VAT',
  labels = {},
  validationScope = 'customer',
}) {
  const dispatch = useDispatch();
  const verifyTrn = useVerifyTrnHandler();
  const [trnState, setTrnState] = useState({ kind: 'idle' });
  const [dialogResult, setDialogResult] = useState(null);
  const skipResetRef = useRef(false);

  const trnValue = watch(name) ?? '';
  const partyName = watch(partyNameField) ?? '';
  const { validations } = useTranslatedMessages();
  const vatFormatMessage = validations?.[validationScope]?.VAT_format;

  useEffect(() => {
    if (skipResetRef.current) {
      skipResetRef.current = false;
      return;
    }
    setTrnState({ kind: 'idle' });
    setDialogResult(null);
  }, [trnValue, partyName]);

  const handleValidate = async () => {
    const trn = String(trnValue).trim();
    const enteredName = String(partyName).trim();

    if (!TRN_PATTERN.test(trn)) {
      setError?.(name, {
        type: 'manual',
        message: vatFormatMessage ?? 'VAT must start with 1 and end with 03',
      });
      return;
    }

    clearErrors?.(name);
    setTrnState({ kind: 'loading' });

    try {
      const response = await verifyTrn.mutateAsync({
        company_name: enteredName,
        trn_no: trn,
      });
      const names = resolveTrnRegistryNames(response);

      if (!enteredName) {
        setTrnState({ kind: 'idle' });
        setDialogResult({ kind: 'lookup', names });
        return;
      }

      if (isTrnNameMatched(response?.name_matched)) {
        const matchName = names[0] || enteredName;
        setTrnState({ kind: 'match', name: matchName });
        setDialogResult({ kind: 'match', matchName });
        return;
      }

      const registered = names[0] || response?.trn_company_name || '';
      setTrnState({ kind: 'mismatch', registered });
      setDialogResult({ kind: 'mismatch', enteredName, names });
    } catch (error) {
      setTrnState({ kind: 'idle' });
      const message =
        error?.response?.data?.error || labels.verifyFailed || 'TRN verification failed. Please try again.';
      dispatch(showSnackbar({ message, severity: 'error' }));
    }
  };

  const mismatchHelper =
    trnState.kind === 'mismatch'
      ? (labels.mismatchHelper ?? 'Mismatch — TRN is registered to {name}').replace(
          '{name}',
          trnState.registered || '—',
        )
      : undefined;

  const endAdornment = readOnly ? undefined : (
    <InputAdornment position="end">
      {trnState.kind === 'match' && <CheckIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />}
      {trnState.kind === 'mismatch' && <CloseIcon color="error" fontSize="small" sx={{ mr: 0.5 }} />}
      <Button
        type="button"
        size="small"
        onClick={handleValidate}
        disabled={trnState.kind === 'loading' || verifyTrn.isPending}
        sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
      >
        {trnState.kind === 'loading' || verifyTrn.isPending ? (
          <CircularProgress size={16} />
        ) : (
          (labels.validate ?? 'Validate')
        )}
      </Button>
    </InputAdornment>
  );

  return (
    <Box>
      <CustomTextInput
        data-testid="tax-identifier-input"
        name={name}
        control={control}
        errors={errors}
        readOnly={readOnly}
        label={label}
        inputProps={{ maxLength: 15 }}
        endAdornment={endAdornment}
        helperText={mismatchHelper}
      />
      {!mismatchHelper && trnState.kind === 'match' && (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
          {(labels.verifiedHelper ?? 'Verified — matches {name}').replace('{name}', trnState.name)}
        </Typography>
      )}
      <TrnVerifyResultsModal
        open={!!dialogResult}
        onClose={() => setDialogResult(null)}
        result={dialogResult}
        trn={String(trnValue).trim()}
        labels={{
          matchTitle: labels.matchTitle ?? 'TRN Match',
          mismatchTitle: labels.mismatchTitle ?? 'TRN Mismatch',
          lookupTitle: labels.lookupTitle ?? 'TRN Lookup Results',
          matchDescription:
            labels.matchDescription ?? 'TRN {trn} is registered to {name} — matches the business name entered.',
          mismatchDescription:
            labels.mismatchDescription ??
            '{name} is not registered against TRN {trn}. Select a registered name to populate the business name.',
          lookupDescription:
            labels.lookupDescription ??
            'Businesses registered with TRN {trn}. Select one to populate the business name.',
          close: labels.close ?? 'Close',
        }}
        onSelectName={(selectedName) => {
          skipResetRef.current = true;
          setValue(partyNameField, selectedName, { shouldDirty: true, shouldValidate: true });
          setTrnState({ kind: 'match', name: selectedName });
          setDialogResult(null);
          dispatch(
            showSnackbar({
              message: labels.populatedSuccess ?? 'Business name populated from TRN registry',
              severity: 'success',
            }),
          );
        }}
      />
    </Box>
  );
}
